import { PropsWithChildren, useEffect } from "react";

import usePublicVenueService from "@app-contexts/public/features/org-profile/data/services/usePublicVenueService";
import styled from "@emotion/styled";
import useAnalytics from "@shared/lib/analytics/hooks/useAnalytics";
import { common, enums } from "@shared/constants";
import useNativeAppCheck from "@shared/hooks/platform/useNativeAppCheck";
import { PublicBaseEventSerializer } from "@shared/modules/events/types/public";
import { PublicMembershipGroupSerializer } from "@shared/modules/memberships/types";
import { SeatingStateProvider } from "@shared/modules/seating/contexts/useSeatingState";
import NavBar from "@ui-application/navigation/NavBar/NavBar.web";
import SEO from "@ui-application/other/SEO/SEO.web";

import { usePublicBasket } from "@app/core/src/app-contexts/public/features/basket/services/usePublicBasket";
import { BasketServiceProvider } from "@shared/modules/basket/context/BasketServiceContext";
import { AnalyticsService } from "@app/core/src/shared/services/analytics/AnalyticsService";
import {
  getSEODescription,
  getSEOTitle,
  shouldDisableSEO,
} from "./DetailPageLayoutHelpers";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  height: fit-content;

  // Adds bottom padding for iOS devices so that page content remains within the phone's safe area
  // (example: iPhones with a notch)
  padding-bottom: env(safe-area-inset-bottom);
`;

type DetailPageLayoutProps = PropsWithChildren<{
  itemData?: PublicBaseEventSerializer | PublicMembershipGroupSerializer;
  analyticsLabel: (typeof enums.INTERACTION_LABEL_MAP)[keyof typeof enums.INTERACTION_LABEL_MAP]["id"];
  trackingTokenLocation: (typeof enums.TRACKING_TOKEN_LOCATIONS)[keyof typeof enums.TRACKING_TOKEN_LOCATIONS]["id"];
}>;

/**
 * Layout component used by [eventSlug] and [membershipSlug] pages.
 */
const DetailPageLayout = ({
  children,
  itemData,
  analyticsLabel,
  trackingTokenLocation,
}: DetailPageLayoutProps) => {
  const { isReactNativeApp } = useNativeAppCheck();
  const { useRetrieve: useRetrieveVenue } = usePublicVenueService();
  const { data: venueData } = useRetrieveVenue(itemData?.venue?.id);
  const analytics = useAnalytics();
  const isEvent = analyticsLabel === enums.INTERACTION_LABEL_MAP.EVENT_PAGE.id;

  const itemAsEvent = isEvent
    ? (itemData as PublicBaseEventSerializer)
    : undefined;
  const itemAsMembership = isEvent
    ? undefined
    : (itemData as PublicMembershipGroupSerializer);

  // Track Facebook ViewContent when event detail page loads
  useEffect(() => {
    if (venueData && itemData && isEvent) {
      AnalyticsService.viewContent({
        label: enums.INTERACTION_LABEL_MAP.EVENT_PAGE.id,
        item: itemData,
        event_id: itemData.id,
        venue_id: venueData.id,
      });
    }
  }, [venueData, itemData, isEvent]);

  useEffect(() => {
    if (venueData && itemData) {
      // Note: viewTicketTypes is now handled by AnalyticsService.viewContent above
      // Removed duplicate analytics.viewTicketTypes call
      // TODO: remove deprecated analytics.trackToken and use AnalyticsService.trackToken instead

      analytics.trackToken(
        trackingTokenLocation,
        venueData.id,
        isEvent ? [itemData?.id] : [],
        {
          fbEvent: "ViewContent",
          content_ids: [itemData.id],
          name: itemData.name,
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueData, itemData]);

  return (
    <BasketServiceProvider basketService={usePublicBasket}>
      <SeatingStateProvider
        isOnSeatingMapPage={false}
        basketType={common.DataProvider.Public}
        key={`assigned-seating-state-${itemData?.id}`}
      >
        <SEO
          shouldDisableSEO={shouldDisableSEO({
            eventData: itemAsEvent,
            membershipGroupData: itemAsMembership,
          })}
          title={getSEOTitle({
            eventData: itemAsEvent,
            membershipGroupData: itemAsMembership,
            venueData,
          })}
          description={getSEODescription({
            eventData: itemAsEvent,
            membershipGroupData: itemAsMembership,
          })}
          metaDescription={getSEODescription({
            eventData: itemAsEvent,
            membershipGroupData: itemAsMembership,
          })}
          image={itemAsEvent?.image_banner ?? itemAsMembership?.image}
        />

        <Container>
          <NavBar isDetailPage isReactNativeApp={isReactNativeApp} />
          {children}
        </Container>
      </SeatingStateProvider>
    </BasketServiceProvider>
  );
};

export default DetailPageLayout;
